import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Checkbox } from 'primereact/checkbox'
import { RadioButton } from 'primereact/radiobutton'
import FormDateField from '../../../components/ui/form/FormDateField'
import { parseISODate } from '../../../lib/datetime'
import { resolveFieldError } from '../../../lib/forms/messages'
import { RECURRENCE_TYPES, type AgendaRecurrence } from '../types'
import type { AgendaControl } from './agendaFormSchema'

const TOGGLE_ID = 'agenda-recurring'
const TYPE_LABEL_ID = 'agenda-recurrence-type-label'
const TYPE_ERR_ID = 'agenda-recurrence-type-err'

interface Props {
  control: AgendaControl
  /** `create`: onay kutusu + sıklık + bitiş; `series`: sıklık salt-okunur, yalnız bitiş düzenlenir. */
  mode: 'create' | 'series'
  /** Seri düzenlemesinde serinin sıklığı (salt-okunur gösterim). */
  seriesType?: AgendaRecurrence
}

/**
 * Tekrarlı toplantı alanları (126). Davetli alanlarının altında durur (spec FR-001). Bitiş tarihinin en erken
 * günü takvimde de kısıtlanır: oluşturmada ilk günden sonrası, seri düzenlemesinde düzenlenen gün.
 */
export default function AgendaRecurrenceFields({ control, mode, seriesType }: Readonly<Props>) {
  const { t } = useTranslation()
  const recurring = useWatch({ control, name: 'recurring' })
  const startDate = useWatch({ control, name: 'startDate' })

  return (
    <div className="ui-field full agenda-recurrence">
      {mode === 'create' ? (
        <Controller
          control={control}
          name="recurring"
          render={({ field }) => (
            <span className="admin-form-check">
              <Checkbox
                inputId={TOGGLE_ID}
                checked={field.value}
                onChange={(e) => field.onChange(Boolean(e.checked))}
              />
              <label htmlFor={TOGGLE_ID}>{t('agenda.recurrence.toggle')}</label>
            </span>
          )}
        />
      ) : (
        <p className="agenda-recurrence-current">
          <i className="pi pi-sync" aria-hidden="true" />
          <span className="agenda-muted">{t('agenda.recurrence.label')}:</span>{' '}
          {seriesType ? t(`agenda.recurrence.types.${seriesType}`) : ''}
        </p>
      )}
      {recurring && (
        <div className="agenda-recurrence-body">
          {mode === 'create' && <FrequencyField control={control} />}
          <FormDateField
            control={control}
            name="recurrenceEndDate"
            label={t('agenda.recurrence.endDate')}
            required
            minDate={minEndDate(startDate, mode)}
          />
          <p className="agenda-muted agenda-recurrence-hint">{t('agenda.recurrence.hint')}</p>
        </div>
      )}
    </div>
  )
}

/** Sıklık: tek seçimli grup (görseldeki üç kutu birbirini dışlar). */
function FrequencyField({ control }: Readonly<{ control: AgendaControl }>) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name="recurrenceType"
      render={({ field, fieldState }) => (
        <div className="ui-field">
          <span className="ui-field-label" id={TYPE_LABEL_ID}>
            {t('agenda.recurrence.frequency')} <span className="ui-req">*</span>
          </span>
          <div
            className="agenda-recurrence-options"
            role="radiogroup"
            aria-labelledby={TYPE_LABEL_ID}
            aria-describedby={TYPE_ERR_ID}
          >
            {RECURRENCE_TYPES.map((type) => (
              <div key={type} className="agenda-recurrence-option">
                <RadioButton
                  inputId={`agenda-recurrence-${type}`}
                  name="agendaRecurrence"
                  checked={field.value === type}
                  onChange={() => field.onChange(type)}
                />
                <label htmlFor={`agenda-recurrence-${type}`}>{t(`agenda.recurrence.types.${type}`)}</label>
              </div>
            ))}
          </div>
          <div className="ui-field-err" id={TYPE_ERR_ID} aria-live="polite">
            {resolveFieldError(fieldState.error?.message, t)}
          </div>
        </div>
      )}
    />
  )
}

/** Takvimde seçilebilecek en erken bitiş günü (kuralın kendisi şemada). */
function minEndDate(startDate: string, mode: 'create' | 'series'): Date | undefined {
  const start = parseISODate(startDate)
  if (!start) return undefined
  if (mode === 'series') return start
  const next = new Date(start)
  next.setDate(next.getDate() + 1)
  return next
}
